import * as React from 'react';

import type { Page } from './types';

type Query = Record<string, string | number | boolean | null | undefined>;

/** A cursor-paged Developer list (the contract's `{results, next_cursor}`): the first page loads
 *  whenever the filters change, `loadMore` appends the next one. A reply that lands after the filters
 *  changed again is dropped, so a slow first page never overwrites a newer one. */
export function useCursorList<T, P extends Page<T> = Page<T>>(fetchPage: (query: Query) => Promise<P>, filters: Query) {
    const [items, setItems] = React.useState<T[]>([]);
    const [page, setPage] = React.useState<P | null>(null);
    const [nextCursor, setNextCursor] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<unknown>(null);
    const generation = React.useRef(0);
    const fetchRef = React.useRef(fetchPage);
    fetchRef.current = fetchPage;
    const filtersKey = JSON.stringify(filters);

    const load = React.useCallback((cursor: string | null) => {
        const mine = cursor ? generation.current : ++generation.current;
        setLoading(true);
        setError(null);
        fetchRef.current({ ...JSON.parse(filtersKey), cursor })
            .then((res) => {
                if (mine !== generation.current) {
                    return;
                }
                setPage(res);
                setItems((prev) => (cursor ? [...prev, ...(res.results || [])] : (res.results || [])));
                setNextCursor(res.next_cursor || null);
            })
            .catch((e) => {
                if (mine === generation.current) {
                    setError(e);
                }
            })
            .finally(() => {
                if (mine === generation.current) {
                    setLoading(false);
                }
            });
    }, [filtersKey]);

    React.useEffect(() => {
        load(null);
    }, [load]);

    return {
        items,
        page,
        loading,
        error,
        hasMore: !!nextCursor,
        loadMore: () => nextCursor && load(nextCursor),
        reload: () => load(null),
    };
}
